import React, { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

import TableContainer from "Components/Common/TableContainer";
import Flatpickr from "react-flatpickr";
import {
  getAgencyLTAs,
  deleteAgencyLTA,
  getAgencyLTASummary,
  importAgencyLTA,
  exportAgencyLTAsToCSV,
  LTA_POPULATE,
} from "common/services/ltas.service";
import { getSortObj } from "common/lib/tableDataOptions";
import {
  createTableSearchParams,
  parseTableFilters,
  parseTableSearch,
  parseTableSort,
} from "common/lib/tableQueryParams";
import * as moment from "moment";
import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";
import DeleteModal from "Components/Common/DeleteModal";
import { normalize } from "common/lib/normalize";
import { toast } from "react-toastify";
import BreadCrumb from "Components/Common/BreadCrumb";
import { useProfile } from "Components/Hooks/UserHooks";

import LTASummaryWidgets from "pages/LTAs/components/LTASummaryWidgets";
import LTAStatusBadge from "pages/LTAs/components/LTAStatusBadge";
import {
  LTAServiceFilter,
  LTAStatusFilter,
} from "pages/LTAs/components/LTAFilters";
import {
  statusFilterConditions,
  termProgress,
} from "pages/LTAs/components/ltaOptions";
import AddLTA from "./AddLTA";
import UpdateLTA from "./UpdateLTA";
import Import from "pages/LTAs/Import";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  // Selects hold the whole `{ value, label }` option so the choice survives a
  // page reload from the URL without a second lookup.
  ltaService: any;
  ltaStatus: any;
  dateRange: DateRange;
}

const defaultFilterValue: FilterValue = {
  ltaService: null,
  ltaStatus: null,
  dateRange: { fromDate: null, toDate: null },
};

const TermCell = ({ lta }: { lta: any }) => {
  const progress = termProgress(lta?.startDate, lta?.endDate);

  if (!lta?.startDate && !lta?.endDate) {
    return <span className="text-muted">Not Provided</span>;
  }

  return (
    <div style={{ minWidth: "180px" }}>
      <div className="d-flex justify-content-between fs-12 mb-1">
        <span>
          {lta?.startDate
            ? moment(new Date(lta.startDate)).format("DD MMM Y")
            : "—"}
        </span>
        <span className="text-muted">
          {lta?.endDate ? moment(new Date(lta.endDate)).format("DD MMM Y") : "—"}
        </span>
      </div>
      {progress !== null && (
        <div className="progress" style={{ height: "4px" }}>
          <div
            className={`progress-bar bg-${
              progress >= 90 ? "danger" : progress >= 75 ? "warning" : "success"
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

/** The single service the agreement covers. */
const ServiceCell = ({ service }: { service: any }) => {
  if (!service?.name) {
    return <span className="text-muted">Not Provided</span>;
  }
  return (
    <span className="badge bg-primary-subtle text-primary fs-11">
      {service.name}
    </span>
  );
};

const AgencyLTAs = () => {
  const location = useLocation();
  usePageActivity(location.pathname, "Agency LTAs");
  const { userProfile } = useProfile();

  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || 20,
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1,
    pageSize: params.pageSize,
  });

  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  const [sorted, setSorted] = useState(() =>
    parseTableSort(searchParams, [{ id: "createdAt", desc: true }])
  );

  const [selectedRecord, setSelectedRecord] = useState<any>({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const showUpdateModalForm = (lta: any) => {
    setSelectedRecord(lta);
    setIsUpdateModalOpen(true);
  };

  const [searchValue, setSearchValue] = useState(
    parseTableSearch(searchParams)
  );
  const [filterValue, setFilterValue] = useState(() =>
    parseTableFilters(searchParams, defaultFilterValue)
  );

  const resetTable = () => {
    setPage(1);
    setSorted([{ id: "createdAt", desc: true }]);
    setSearchValue("");
    setFilterValue(defaultFilterValue);
  };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const [apiParams, setApiParams] = useState<any>({});

  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = createTableSearchParams({
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
      });
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      // Scoping to the caller's agency is applied by the backend, so no
      // agency condition is added here.
      let andConditions: any[] = [];

      if (filterValue?.ltaService?.value) {
        andConditions.push({
          ltaService: { id: { $eq: filterValue.ltaService.value } },
        });
      }
      if (filterValue?.ltaStatus?.value) {
        // Status is derived from the term, so it filters on the dates.
        andConditions.push(
          ...statusFilterConditions(filterValue.ltaStatus.value)
        );
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        const fromDate = new Date(
          filterValue.dateRange.fromDate
        ).toLocaleDateString("en-CA");
        const toDate = new Date(
          filterValue.dateRange.toDate
        ).toLocaleDateString("en-CA");
        andConditions.push({
          startDate: { $lte: toDate },
          endDate: { $gte: fromDate },
        });
      }

      andConditions.push({
        $or: [
          { title: { $containsi: searchValue } },
          { ltaNumber: { $containsi: searchValue } },
          { contractor: { contractorName: { $containsi: searchValue } } },
        ],
      });

      try {
        setLoading(true);
        const apiParams = {
          pagination: { page, pageSize: pagination.pageSize },
          sort: sortObj,
          populate: [...LTA_POPULATE, "contracts"],
          filters: { $and: andConditions },
        };
        setApiParams(apiParams);
        const response: any = await getAgencyLTAs(apiParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, setSearchParams]
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = useCallback(
    (data: any) => {
      setSelectedRecord(data);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
      filterValue: filterValue,
    });
  }, [onPageChange, pagination.pageIndex, sorted, searchValue, filterValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deleteAgencyLTA(selectedRecord.id);
        fetchUpdatedData();
        setDeleteModal(false);
        toast.success("LTA deleted successfully");
      } catch (e: any) {
        toast.error("Error while deleting LTA");
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Agreement",
        accessorKey: "title",
        id: "title",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const row = cell.row.original;
          return (
            <div style={{ whiteSpace: "pre-wrap", width: "300px" }}>
              <Link
                to={`/agency-data/ltas/${row?.id}`}
                className="fw-medium link-primary d-block"
              >
                {cell.getValue() || "Untitled agreement"}
              </Link>
              <span className="text-muted fs-12">
                <i className="ri-hashtag align-bottom"></i>
                {row?.ltaNumber || "No number"}
              </span>
            </div>
          );
        },
      },
      {
        header: "Contractor",
        accessorKey: "contractor.contractorName",
        id: "contractor.contractorName",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const contractor = cell.row.original?.contractor;
          if (!contractor)
            return <span className="text-muted">Not Provided</span>;
          return (
            <Link
              to={`/contractors/${contractor.id}`}
              className="text-body fw-medium"
              style={{
                whiteSpace: "pre-wrap",
                display: "inline-block",
                width: "200px",
              }}
            >
              {contractor.contractorName}
            </Link>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "endDate",
        id: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <LTAStatusBadge
            startDate={cell.row.original?.startDate}
            endDate={cell.row.original?.endDate}
            showCountdown
          />
        ),
      },
      {
        header: "Term",
        accessorKey: "startDate",
        id: "startDate",
        enableColumnFilter: false,
        cell: (cell: any) => <TermCell lta={cell.row.original} />,
      },
      {
        header: "LTA Service",
        accessorKey: "ltaService",
        id: "ltaService",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => <ServiceCell service={cell.getValue()} />,
      },
      {
        header: "Lead Agency",
        accessorKey: "leadAgency.name",
        id: "leadAgency.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const row = cell.row.original;
          return (
            <div>
              <div>{cell.getValue() || "Not Provided"}</div>
              {row?.isUsableByOtherAgencies && (
                <span className="badge bg-info-subtle text-info fs-11 mt-1">
                  <i className="ri-share-forward-line align-bottom me-1"></i>
                  Open to all
                </span>
              )}
            </div>
          );
        },
      },
      {
        header: "Contracts",
        accessorKey: "contracts",
        id: "contracts",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const contracts = cell.getValue() || [];
          const total = contracts.reduce(
            (sum: number, c: any) => sum + (c?.contractValue || 0),
            0
          );
          return (
            <div>
              <span className="fw-medium">{contracts.length}</span>
              {total > 0 && (
                <div className="text-muted fs-12">
                  ${total.toLocaleString()}
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="lta" controller="agency" action="findOne">
                  <DropdownItem href={`/agency-data/ltas/${rowData.id}`}>
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>

                <Permission resource="lta" controller="agency" action="update">
                  <DropdownItem
                    href="/#"
                    onClick={(e) => {
                      e.preventDefault();
                      showUpdateModalForm({ id: rowData.id, ...rowData });
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                </Permission>

                <Permission resource="lta" controller="agency" action="delete">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [onClickDelete]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={`${userProfile?.agency?.name || ""} - Long Term Agreements`}
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />
          <DeleteModal
            show={deleteModal}
            onDeleteClick={() => handleDelete()}
            onCloseClick={() => setDeleteModal(false)}
            loading={deleting}
          />

          <LTASummaryWidgets fetchSummary={getAgencyLTASummary} />

          <Card>
            <CardBody>
              <Row className="g-3">
                <Col xxl={3} lg={6}>
                  <label
                    htmlFor="agency-lta-search"
                    className="form-label text-uppercase text-muted fw-medium fs-11 mb-1"
                  >
                    Search
                  </label>
                  <div className="search-box">
                    <input
                      id="agency-lta-search"
                      type="text"
                      className="form-control search"
                      placeholder="Search by title, number or contractor..."
                      onChange={(e) => {
                        setSearchValue(e.target.value);
                        setPage(1);
                      }}
                      value={searchValue}
                    />
                    <i className="ri-search-line search-icon"></i>
                    {searchValue ? (
                      <button
                        type="button"
                        aria-label="Clear search"
                        title="Clear search"
                        className="btn btn-link text-muted p-0 position-absolute top-50 end-0 translate-middle-y me-3 lh-1"
                        onClick={() => {
                          setSearchValue("");
                          setPage(1);
                        }}
                      >
                        <i className="ri-close-circle-fill fs-16"></i>
                      </button>
                    ) : null}
                  </div>
                </Col>
                <Col xxl={2} lg={6}>
                  <label
                    htmlFor="ltaServiceFilter"
                    className="form-label text-uppercase text-muted fw-medium fs-11 mb-1"
                  >
                    LTA Service
                  </label>
                  <LTAServiceFilter
                    onChange={(value: any) => {
                      setFilterValue({ ...filterValue, ltaService: value });
                      setPage(1);
                    }}
                    value={filterValue.ltaService}
                  />
                </Col>
                <Col xxl={3} lg={6}>
                  <label
                    htmlFor="ltaStatusFilter"
                    className="form-label text-uppercase text-muted fw-medium fs-11 mb-1"
                  >
                    Status
                  </label>
                  <LTAStatusFilter
                    onChange={(value: any) => {
                      setFilterValue({ ...filterValue, ltaStatus: value });
                      setPage(1);
                    }}
                    value={filterValue.ltaStatus}
                  />
                </Col>
                <Col xxl={4} lg={6}>
                  <label
                    htmlFor="agency-lta-date-range"
                    className="form-label text-uppercase text-muted fw-medium fs-11 mb-1"
                  >
                    Active Between
                  </label>
                  <div className="position-relative">
                    {/* @ts-ignore */}
                    <Flatpickr
                      id="agency-lta-date-range"
                      className="form-control"
                      placeholder="Select a date range"
                      options={{
                        mode: "range",
                        altFormat: "d M, Y",
                        dateFormat: "d M, Y",
                      }}
                      onChange={(e) => {
                        if (e[0] && e[1]) {
                          setFilterValue({
                            ...filterValue,
                            dateRange: { fromDate: e[0], toDate: e[1] },
                          });
                          setPage(1);
                        }
                      }}
                      value={[
                        filterValue?.dateRange?.fromDate || "",
                        filterValue?.dateRange?.toDate || "",
                      ]}
                    />
                    {filterValue?.dateRange?.fromDate ? (
                      <button
                        type="button"
                        aria-label="Clear date range"
                        title="Clear date range"
                        className="btn btn-link text-muted p-0 position-absolute top-50 end-0 translate-middle-y me-3 lh-1"
                        onClick={() => {
                          setFilterValue({
                            ...filterValue,
                            dateRange: { fromDate: null, toDate: null },
                          });
                          setPage(1);
                        }}
                      >
                        <i className="ri-close-circle-fill fs-16"></i>
                      </button>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={9}>
                  <h5 className="card-title mb-1">
                    {userProfile?.agency?.name || ""} - Long Term Agreements
                  </h5>
                  <p className="text-muted mb-0">
                    Framework agreements your agency leads or draws on, together
                    with the contracts awarded under them.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2">
                    <Permission
                      resource="lta"
                      controller="agency"
                      action="create"
                    >
                      <Link
                        to="#"
                        className="btn btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsAddModalOpen(true);
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add LTA
                      </Link>
                    </Permission>

                    <Permissions
                      checks={[
                        { resource: "lta", controller: "agency", action: "import" },
                        { resource: "lta", controller: "agency", action: "exportToCsv" },
                      ]}
                    >
                      <Import
                        apiParams={apiParams}
                        controller="agency"
                        importFn={importAgencyLTA}
                        exportFn={exportAgencyLTAsToCSV}
                        onImported={fetchUpdatedData}
                      />
                    </Permissions>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                filterValue={filterValue}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle mb-0 table-striped"
                theadClass="table-light table-nowrap"
              />
              <AddLTA
                toggle={() => setIsAddModalOpen((state) => !state)}
                isModalOpen={isAddModalOpen}
                fetchData={resetTable}
              />
              <UpdateLTA
                toggle={() => setIsUpdateModalOpen((state) => !state)}
                isModalOpen={isUpdateModalOpen}
                selectedRecord={selectedRecord}
                fetchData={fetchUpdatedData}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AgencyLTAs;
